import React from "react";
import styled, { keyframes } from "styled-components";
import { tokens, mq } from "../../theme/tokens";
import logo from "../../Assets/Images/logo.png";

// AuthLayout — split-screen shell for unauthenticated screens (Login, MFA).
// Left: crimson→maroon brand panel with the customer app's speed-streak motif.
// Right: a clean surface that hosts the auth card (passed as children).
// Presentation-only; holds no auth logic.
//
//   <AuthLayout tagline="Operations control for every pickup & drop.">
//       <AuthCard>...form...</AuthCard>
//   </AuthLayout>

const { colors, radii, shadows, spacing, typography, motion } = tokens;

const drift = keyframes`
    from { transform: translateX(-6%) skewX(-18deg); }
    to   { transform: translateX(6%)  skewX(-18deg); }
`;
const rise = keyframes`
    to { opacity: 1; transform: translateY(0); }
`;

const Root = styled.div`
    display: grid;
    grid-template-columns: 1.05fr 1fr;
    min-height: 100vh;
    background: ${colors.cream};
    font-family: ${typography.fonts.body};

    ${mq.down.lg} {
        grid-template-columns: 1fr;
    }
`;

// ---- Brand panel ------------------------------------------------------------
const Brand = styled.aside`
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: ${spacing[8]} ${spacing[7]};
    color: ${colors.onDark};
    background:
        radial-gradient(120% 120% at 15% 10%, ${colors.vermilion} 0%, transparent 45%),
        linear-gradient(155deg, ${colors.crimson} 0%, ${colors.maroon} 55%, ${colors.ink} 100%);

    ${mq.down.lg} {
        padding: ${spacing[6]} ${spacing[5]};
        min-height: 220px;
    }
`;

// Diagonal "speed streaks" à la the customer homepage.
const Streaks = styled.div`
    position: absolute;
    inset: -20% -30%;
    pointer-events: none;
    opacity: 0.5;
    background-image: repeating-linear-gradient(
        -18deg,
        rgba(255, 255, 255, 0) 0px,
        rgba(255, 255, 255, 0) 46px,
        rgba(233, 162, 59, 0.5) 46px,
        rgba(233, 162, 59, 0.5) 48px,
        rgba(255, 255, 255, 0.16) 48px,
        rgba(255, 255, 255, 0.16) 52px,
        rgba(255, 255, 255, 0) 52px,
        rgba(255, 255, 255, 0) 120px
    );
    animation: ${drift} 9s ${motion.ease} infinite alternate;

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

// Soft glow blob for depth.
const Glow = styled.div`
    position: absolute;
    width: 460px;
    height: 460px;
    right: -140px;
    bottom: -160px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(233, 162, 59, 0.35) 0%, transparent 70%);
    pointer-events: none;
`;

const BrandInner = styled.div`
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    gap: ${spacing[4]};
`;

const Mark = styled.div`
    display: inline-flex;
    align-items: center;
    gap: ${spacing[3]};
    img {
        height: 46px;
        width: auto;
        filter: drop-shadow(0 6px 16px rgba(0, 0, 0, 0.3));
        background: #fff;
        padding: 6px 8px;
        border-radius: ${radii.md};
    }
    span {
        font-family: ${typography.fonts.display};
        font-size: ${typography.size.xl};
        letter-spacing: 0.06em;
    }
`;

const Hero = styled.div`
    position: relative;
    z-index: 1;
    max-width: 30ch;

    .eyebrow {
        display: inline-block;
        font-size: ${typography.size.sm};
        font-weight: ${typography.weight.bold};
        letter-spacing: ${typography.tracking.wider};
        text-transform: uppercase;
        color: ${colors.gold};
        margin-bottom: ${spacing[3]};
        opacity: 0;
        transform: translateY(16px);
        animation: ${rise} 0.8s ${motion.ease} 0.1s forwards;
    }
    h1 {
        margin: 0;
        font-family: ${typography.fonts.display};
        font-size: ${typography.size["5xl"]};
        line-height: 0.98;
        letter-spacing: 0.01em;
        text-transform: uppercase;
        opacity: 0;
        transform: translateY(20px);
        animation: ${rise} 0.9s ${motion.ease} 0.22s forwards;
    }
    p {
        margin: ${spacing[4]} 0 0;
        font-size: ${typography.size.lg};
        line-height: ${typography.leading.snug};
        color: ${colors.onDarkMute};
        opacity: 0;
        transform: translateY(20px);
        animation: ${rise} 0.9s ${motion.ease} 0.34s forwards;
    }

    ${mq.down.lg} {
        h1 { font-size: ${typography.size["3xl"]}; }
        p { display: none; }
    }
`;

const Foot = styled.div`
    position: relative;
    z-index: 1;
    font-size: ${typography.size.sm};
    color: ${colors.onDarkMute};
    ${mq.down.lg} { display: none; }
`;

// ---- Form side --------------------------------------------------------------
const Panel = styled.main`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: ${spacing[7]} ${spacing[6]};

    ${mq.down.lg} {
        padding: ${spacing[6]} ${spacing[4]};
    }
`;

const Slot = styled.div`
    width: 100%;
    max-width: 420px;
    opacity: 0;
    transform: translateY(20px);
    animation: ${rise} 0.7s ${motion.ease} 0.28s forwards;
`;

// Reusable auth card the pages drop their form into.
export const AuthCard = styled.div`
    background: ${colors.surface};
    border: 1px solid ${colors.border};
    border-radius: ${radii.lg};
    box-shadow: ${shadows.lg};
    padding: ${spacing[7]} ${spacing[6]};

    ${mq.down.lg} {
        padding: ${spacing[6]} ${spacing[5]};
    }
`;

export const AuthHeading = styled.div`
    margin-bottom: ${spacing[6]};
    h2 {
        margin: 0;
        font-family: ${typography.fonts.body};
        font-size: ${typography.size["2xl"]};
        font-weight: ${typography.weight.extrabold};
        letter-spacing: ${typography.tracking.tight};
        color: ${colors.heading};
    }
    p {
        margin: ${spacing[2]} 0 0;
        font-size: ${typography.size.md};
        color: ${colors.muted};
        line-height: ${typography.leading.snug};
    }
`;

export default function AuthLayout({
    eyebrow = "Admin Portal",
    heading = "Pick’n’Drop",
    tagline = "Operations control for every pickup, transfer & drop — riders, requests and revenue in one place.",
    children,
}) {
    return (
        <Root>
            <Brand>
                <Streaks aria-hidden />
                <Glow aria-hidden />
                <BrandInner>
                    <Mark>
                        <img src={logo} alt="Pick'n'Drop" />
                        <span>PICK&apos;N&apos;DROP</span>
                    </Mark>
                </BrandInner>
                <Hero>
                    <span className="eyebrow">{eyebrow}</span>
                    <h1>{heading}</h1>
                    <p>{tagline}</p>
                </Hero>
                <Foot>&copy; {"Pick’n’Drop Services"} &middot; Secure admin access</Foot>
            </Brand>

            <Panel>
                <Slot>{children}</Slot>
            </Panel>
        </Root>
    );
}
